import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';

import SchedulingForm from '../../order-summary/components/SchedulingForm';
import PropertyAccessForm from '../../order-summary/components/PropertyAccessForm';
import { useOrderSummaryForm } from '../../order-summary/hooks/useOrderSummaryForm';
import type { OrderSummaryFormResult } from '../../order-summary/hooks/useOrderSummaryForm';

import { formatPriceDisplay } from '../../../../util/formatting';
import type { AccessType, PropertyAccess, PropertyCartItem } from '../types/cart.types';

interface CartAppointmentFormProps {
  data: PropertyCartItem;
  onDone: (data: PropertyCartItem) => void;
  onBack: (data: PropertyCartItem) => void;
}

/** Map the validated form result into the cart's PropertyAccess shape. */
const toPropertyAccess = (result: OrderSummaryFormResult): PropertyAccess => ({
  onsiteContactName: result.onsiteContactName,
  onsiteContactPhone: result.onsiteContactPhone,
  accessType: result.accessType as AccessType | null,
  accessCode: result.accessCode,
  lockboxLocation: result.lockboxLocation,
  rentlyRegistrationPin: result.rentlyRegistrationPin,
  isAccessNeeded: result.isAccessNeeded,
  isOnsiteAppointment: result.isOnsiteAppointment,
  specialInstructions: result.specialInstructions,
});

const CartAppointmentForm = ({
  data,
  onDone,
  onBack,
}: CartAppointmentFormProps) => {
  const form = useOrderSummaryForm({
    data,
    orderSummaryInfo: data.propertyAccess,
    isCartOrder: true,
    isCartOrderFirstAvailableDate: data.firstAvailableDate,
    isCartOrderFirstAvailableDateStatus: !data.firstAvailableDate
      ? 'Yes'
      : 'No',
  });

  const handleDone = () => {
    const result = form.tryBuildResult();
    if (!result) return;
    onDone({
      ...data,
      price: data.price,
      isPropertyReady: result.firstAvailableDateStatus === 'Yes',
      firstAvailableDate: result.firstAvailableDate,
      propertyAccess: toPropertyAccess(result),
      specialInstructions: result.specialInstructions,
    });
  };

  return (
    <section className="flex h-full w-full flex-col bg-background">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="space-y-4 px-6 pt-6">
          <SchedulingForm
            firstAvailableDateStatus={form.firstAvailableDateStatus}
            firstAvailableDate={form.firstAvailableDate}
            onStatusChange={form.handleStatusChange}
            onDateChange={form.handleDateChange}
            isCartOrder={true}
            errors={form.errors}
          />
          <PropertyAccessForm
            values={{
              accessType: form.accessType,
              lockboxLocation: form.lockboxLocation,
              accessCode: form.accessCode,
              rentlyRegistrationPin: form.rentlyRegistrationPin,
              contactName: form.contactName,
              contactNumber: form.contactNumber,
              additionalNotes: form.additionalNotes,
            }}
            onChange={form.handleAccessFieldChange}
            errors={form.errors}
            isCartOrder={true}
            defaultNotes={form.defaultNotes}
          />
        </div>
      </div>

      <div className="shrink-0 flex items-center border-t border-border bg-background px-5 py-3.5">
        <Button
          variant="outline"
          className="min-w-[100px]"
          onClick={() => onBack(data)}
        >
          Back
        </Button>
        <div className="ml-auto flex items-center gap-5">
          <div className="flex items-baseline gap-1.5">
            <Typography variant="body-medium" className="text-muted-foreground">
              Total
            </Typography>
            <Typography variant="heading-md">
              ${formatPriceDisplay(form.accessInfoTotal)}
            </Typography>
          </div>
          <Button className="min-w-[100px]" onClick={handleDone}>
            Done
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CartAppointmentForm;
